import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { XIcon } from 'lucide-react';
import { LoadingLabel, MockdLogo } from '../components/common';
import { useAuth } from '../hooks';
import { ApiError } from '../utils/apiClient';

const ERROR_MESSAGES: Record<string, string> = {
  invalid_token: 'This link is invalid. Please request a new one.',
  token_used: 'This link has already been used. Please request a new one.',
  token_expired: 'This link has expired. Please request a new one.',
};

// The magic link lands here, and signing in waits for a click. Mail scanners
// open links in email (some run the page's JavaScript too), and a link that
// signed in on load would be spent before the user ever saw it.
export function AuthVerify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyMagicLink } = useAuth();
  const token = searchParams.get('token');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(token ? null : 'No sign-in token provided.');

  const handleContinue = async () => {
    if (!token) return;
    setSubmitting(true);
    try {
      const redirect = await verifyMagicLink(token);
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError && err.code && ERROR_MESSAGES[err.code]
          ? ERROR_MESSAGES[err.code]
          : 'An error occurred. Please try again.',
      );
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="card bg-base-100 p-6">
            <div className="w-12 h-12 mx-auto mb-4 bg-error/20 rounded-full flex items-center justify-center">
              <XIcon className="size-6 text-error" />
            </div>
            <h1 className="text-base-content mb-2">Verification Failed</h1>
            <p className="text-base-content/80 mb-6">{error}</p>
            <Link
              to="/login"
              className="btn btn-primary w-full"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="mb-8">
          <MockdLogo size="md" className="mx-auto" />
        </div>
        <div className="card bg-base-100 p-6">
          <h1 className="text-base-content mb-2">Sign in to mockd</h1>
          <p className="text-base-content/80 mb-6">Continue to finish signing in from your email link.</p>
          <button
            type="button"
            className="btn btn-primary w-full"
            onClick={handleContinue}
            disabled={submitting}
          >
            {submitting ? <LoadingLabel>Signing in</LoadingLabel> : 'Continue to mockd'}
          </button>
        </div>
      </div>
    </div>
  );
}
